"use client";

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import DOMPurify from "dompurify";
import { Form, Input } from "antd";
import { createUseStyles } from "react-jss";

import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
} from "lexical";
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  ListNode,
  ListItemNode,
} from "@lexical/list";
import { $generateNodesFromDOM, $generateHtmlFromNodes } from "@lexical/html";
import { $patchStyleText } from "@lexical/selection";

const stripArtifacts = (s = "") =>
  String(s)
    .replace(/(?:\r\n|\r|\n)/g, "")
    .replace(/\\r\\n|\\r|\\n|\/n/g, "")
    .replace(/>\s+</g, "><");

const looksLikeHtml = (s = "") => /<\/?[a-z][\s\S]*>/i.test(String(s));

const looksLikeBase64 = (s = "") => {
  const v = String(s || "").trim();
  if (v.length < 16) return false;
  if (looksLikeHtml(v)) return false;
  return /^[A-Za-z0-9+/=\s]+$/.test(v) && v.length % 4 === 0;
};

const decodeBase64Unicode = (str = "") => {
  try {
    const bytes = Uint8Array.from(atob(str), (c) => c.charCodeAt(0));
    return new TextDecoder("utf-8").decode(bytes);
  } catch {
    return "";
  }
};

const escapeHtml = (text = "") =>
  String(text)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const decodeHtmlEntities = (value = "") => {
  try {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = String(value);
    return textarea.value;
  } catch {
    return String(value);
  }
};

const normalizePastedFormatting = (value = "") => {
  let normalized = decodeHtmlEntities(value)
    .replace(/\u00a0/g, " ")
    .replace(/\\r\\n|\\n|\\r/g, "\n");

  normalized = normalized.replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>");
  return normalized;
};

const normalizeToHtml = (value) => {
  if (value === null || value === undefined) return "";

  let v = normalizePastedFormatting(value).trim();
  if (!v || v === "null" || v === "undefined") return "";

  if (looksLikeBase64(v)) {
    const decoded = decodeBase64Unicode(v);
    if (decoded) v = decoded.trim();
  }

  if (!looksLikeHtml(v)) {
    const safe = escapeHtml(v).replaceAll("\n", "<br/>");
    v = `<p>${safe}</p>`;
  }

  const cleaned = DOMPurify.sanitize(v, {
    ALLOWED_TAGS: ["p", "br", "b", "strong", "i", "em", "u", "ul", "ol", "li"],
    ALLOWED_ATTR: [],
  });

  return cleaned;
};


const htmlToPlainText = (html = "") => {
  try {
    const safe = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
    const ta = document.createElement("textarea");
    ta.innerHTML = safe;
    return (ta.value || "").replace(/\s+/g, " ").trim();
  } catch {
    return String(html)
      .replace(/<\/?[^>]+(>|$)/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }
};

function ToolbarPlugin({ classes, disabled, source }) {
  const [editor] = useLexicalComposerContext();
  const fmt = (type) => editor.dispatchCommand(FORMAT_TEXT_COMMAND, type);

  const onSetFontSize = (fontSizePx) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      $patchStyleText(selection, { "font-size": fontSizePx });
    });
  };

  return (
    <div className={source === "JobPostForm" || source === "ApplyJob" ? classes.toolbarJobPost : classes.toolbarClassName}>
      <button
        type="button"
        disabled={disabled}
        className={classes.btn}
        onClick={() => fmt("bold")}
      >
        B
      </button>
      <button
        type="button"
        disabled={disabled}
        className={classes.btn}
        onClick={() => fmt("italic")}
      >
        I
      </button>
      <button
        type="button"
        disabled={disabled}
        className={classes.btn}
        onClick={() => fmt("underline")}
      >
        U
      </button>

      <span className={classes.sep} />

      <button
        type="button"
        disabled={disabled}
        className={classes.btn}
        onClick={() =>
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
        }
      >
        • List
      </button>

      <button
        type="button"
        disabled={disabled}
        className={classes.btn}
        onClick={() =>
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
        }
      >
        1. List
      </button>

      <button
        type="button"
        disabled={disabled}
        className={classes.btn}
        onClick={() => editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)}
      >
        Remove List
      </button>

      <span className={classes.sep} />

      <select
        disabled={disabled}
        className={classes.fontSizeSelect}
        defaultValue="14px"
        onChange={(e) => onSetFontSize(e.target.value)}
      >
        {["8px", "9px", "10px", "11px", "12px", "14px", "16px", "18px", "24px", "30px"].map(
          (px) => (
            <option key={px} value={px}>
              {px.replace("px", "")}
            </option>
          )
        )}
      </select>
    </div>
  );
}

function LoadHtmlPlugin({ initialHtml, loadKey }) {
  const [editor] = useLexicalComposerContext();
  const didLoadRef = useRef(false);

  useEffect(() => {
    didLoadRef.current = false;
  }, [loadKey]);

  useEffect(() => {
    if (didLoadRef.current) return;
    didLoadRef.current = true;

    editor.update(() => {
      const root = $getRoot();
      root.clear();

      if (!initialHtml) return;

      const parser = new DOMParser();

      const safeHtml = `<div>${initialHtml}</div>`;
      const dom = parser.parseFromString(safeHtml, "text/html");

      const nodes = $generateNodesFromDOM(editor, dom);

      root.append(...nodes);
    });
  }, [editor, initialHtml, loadKey]);

  return null;
}


const UIRichTextEditor = ({
  form,
  label,
  name,
  required,
  rules,
  onChange = () => { },
  validateStatus,
  hasFeedback,
  help,
  disabled,
  defaultValue,
  placeholder = "",
  showCharCount = false,
  maxChars,
  loadKey,
  source,
}) => {
  const classes = useStyles();

  const initialHTML = useMemo(() => {
    const normalized = normalizeToHtml(defaultValue);
    return stripArtifacts(normalized);
  }, [defaultValue]);

  const baseKey =
    loadKey ?? (Array.isArray(name) ? name.join("_") : String(name)) ?? "";
  const effectiveLoadKey = String(loadKey ?? baseKey ?? "");

  const [charCount, setCharCount] = useState(() =>
    htmlToPlainText(initialHTML).length
  );

  const setField = useCallback(
    (fieldName, value) => {
      if (!form || !fieldName) return;

      if (typeof form.setFieldValue === "function") {
        form.setFieldValue(fieldName, value);
      } else if (typeof form.setFieldsValue === "function") {
        form.setFieldsValue({ [fieldName]: value });
      } else {
        form.setFields([{ name: fieldName, value }]);
      }
    },
    [form]
  );

  const initialConfig = useMemo(
    () => ({
      namespace: "UIRichTextEditorLexical",
      editable: !disabled,
      nodes: [ListNode, ListItemNode],
      onError(error) {
        console.error(error);
      },
    }),
    [disabled]
  );

  useEffect(() => {
    const cleaned = stripArtifacts(initialHTML || "");
    setCharCount(htmlToPlainText(cleaned).length);

    if (form && name) {
      setField(name, cleaned);
    }
  }, [initialHTML, form, name, setField]);

  const pushToFormAndCallback = useCallback(
    (html) => {
      const cleaned = stripArtifacts(normalizePastedFormatting(html));
      const plain = htmlToPlainText(cleaned);
      setCharCount(plain.length);

      if (form && name) {
        setField(name, cleaned);
        onChange?.({ target: { id: name, value: cleaned } });
      } else {
        onChange?.(cleaned);
      }
    },
    [form, name, onChange, setField]
  );

  const handleEditorChange = useCallback(
    (editorState, editor) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor, null);
        pushToFormAndCallback(html);
      });
    },
    [pushToFormAndCallback]
  );

  const overLimit = typeof maxChars === "number" && charCount > maxChars;

  return (
    <Form.Item
      label={label}
      required={required}
      hasFeedback={hasFeedback}
      help={help || (overLimit ? `Max ${maxChars} characters allowed` : undefined)}
      validateStatus={validateStatus || (overLimit ? "error" : undefined)}
    >
      {form && name ? (
        <Form.Item
          name={name}
          hidden
          rules={[
            ...(rules || []),
            ...(typeof maxChars === "number"
              ? [
                {
                  validator: async () => {
                    if (charCount > maxChars) {
                      throw new Error(`Max ${maxChars} characters allowed`);
                    }
                  },
                },
              ]
              : []),
          ]}
        >
          <Input />
        </Form.Item>
      ) : null}

      <div className={classes.wrapperClassName}>
        <LexicalComposer key={effectiveLoadKey} initialConfig={initialConfig}>
          <ToolbarPlugin classes={classes} disabled={disabled} source={source} />

          <div className={classes.editorShell}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable className={source === "ApplyJob" ? classes.editorClassNameApply : source === "JobPostForm" ? classes.editorClassNameJobPost : classes.editorClassName} />
              }
              placeholder={
                <div className={classes.placeholder}>{placeholder}</div>
              }
              ErrorBoundary={({ children }) => children}
            />
            <HistoryPlugin />
            <ListPlugin />
            <LoadHtmlPlugin initialHtml={initialHTML} loadKey={effectiveLoadKey} />
            <OnChangePlugin onChange={handleEditorChange} />
          </div>

          {showCharCount && (
            <div className={classes.charCount}>
              {typeof maxChars === "number"
                ? `${charCount} / ${maxChars}`
                : `${charCount} characters`}
            </div>
          )}
        </LexicalComposer>
      </div>
    </Form.Item>
  );
};

export default UIRichTextEditor;

const useStyles = createUseStyles({
  wrapperClassName: {},
  toolbarClassName: {
    border: "1px solid #f1f1f1",
    padding: 6,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
    borderRadius: 6,
    marginBottom: 8,
    background: "#fff",
  },
  toolbarJobPost: {
    padding: 6,
    paddingLeft: 12,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 8,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 22,
    height: 42,
    display: "flex",
    alignItems: "center",
    boxShadow: "0 12px 35px rgba(15, 23, 42, 0.04)",
  },
  btn: {
    border: "1px solid #e5e5e5",
    background: "#fff",
    borderRadius: 6,
    padding: "4px 8px",
    cursor: "pointer",
    fontSize: 12,
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  sep: { width: 1, height: 18, background: "#eee", margin: "0 4px" },
  fontSizeSelect: {
    border: "1px solid #e5e5e5",
    borderRadius: 6,
    padding: "4px 8px",
    background: "#fff",
  },
  editorShell: { position: "relative" },
  editorClassName: {
    border: "1px solid #f1f1f1",
    height: 300,
    minHeight: 200,
    maxHeight: 300,
    overflowY: "auto",
    overflowX: "hidden",
    background: "#FFFFFF",
    padding: 8,
    borderRadius: 6,
    outline: "none",
    boxSizing: "border-box",
  },
  editorClassNameJobPost: {
    border: "1px solid #f1f1f1",
    height: 300,
    minHeight: 200,
    maxHeight: 300,
    overflowY: "auto",
    overflowX: "hidden",
    background: "#FFFFFF",
    padding: 12,
    paddingLeft: 16,
    borderRadius: 6,
    outline: "none",
    boxSizing: "border-box",
    border: "1px solid #e5e7eb",
    borderRadius: 22,
    boxShadow: "0 12px 35px rgba(15, 23, 42, 0.04)",
  },
  editorClassNameApply: {
    height: 180,
    minHeight: 150,
    maxHeight: 220,
    overflowY: "auto",
    overflowX: "hidden",
    background: "#FFFFFF",
    padding: "12px 16px",
    outline: "none",
    boxSizing: "border-box",
    border: "1px solid #e5e7eb",
    borderRadius: 22,
    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.04)",
  },
  placeholder: {
    position: "absolute",
    top: 10,
    left: 12,
    color: "#999",
    pointerEvents: "none",
    fontSize: 13,
  },
  charCount: { marginTop: 6, fontSize: 12, textAlign: "right", color: "#666" },
});
